import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const JUDGE_ME_REVIEWS_URL = 'https://judge.me/api/v1/reviews';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLElement} message
 * @property {HTMLButtonElement} [submitButton]
 *
 * @extends DialogComponent<Refs>
 */
export default class ReviewForm extends DialogComponent {
  // Literal array (not a spread of the inherited ['dialog']) — mirrors
  // laken-recommendation-popup.js's own convention: simpler to read and
  // avoids relying on base/derived class field initialization order.
  requiredRefs = ['dialog', 'form', 'message'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener('submit', this.#handleSubmit);
    // DialogComponent.closeDialog is an arrow-function instance field, not a
    // prototype method, so it can't be overridden and called via `super` in
    // a subclass (confirmed: `super.closeDialog()` throws "is not a
    // function" for arrow-field methods) — resetting via the dialog:close
    // event it dispatches at the end of closing is the supported extension
    // point instead.
    this.addEventListener(DialogCloseEvent.eventName, this.#resetForm);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener(DialogCloseEvent.eventName, this.#resetForm);
  }

  /**
   * Resets the form to a blank, submittable state after the closing
   * animation finishes, so reopening the dialog never shows a stale
   * success/error message or a disabled submit button left over from a
   * previous visit.
   */
  #resetForm = () => {
    this.refs.form.reset();
    this.refs.form.hidden = false;
    this.refs.message.hidden = true;
    this.refs.message.textContent = '';
    this.#setSubmitting(false);
  };

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.refs.form;
    const rating = form.querySelector('input[name="rating"]:checked');

    if (!(rating instanceof HTMLInputElement)) {
      this.#showMessage(
        Theme.translations?.review_form_rating_required || 'Please select a rating.',
        'error'
      );
      return;
    }

    const formData = new FormData(form);
    const payload = {
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      id: this.dataset.productId,
      name: formData.get('name'),
      email: formData.get('email'),
      rating: rating.value,
      title: formData.get('title'),
      body: formData.get('body'),
    };

    this.#setSubmitting(true);

    try {
      // Sent as application/x-www-form-urlencoded (a CORS-safelisted content
      // type) rather than JSON: Judge.me's endpoint doesn't answer OPTIONS
      // preflight requests (404s them), so a JSON body — which forces a
      // preflight — is silently blocked by the browser before it ever
      // reaches Judge.me. Confirmed against the live API during QA.
      const body = new URLSearchParams();
      for (const [key, value] of Object.entries(payload)) {
        if (value === null || value === undefined || value === '') continue;
        body.append(key, String(value));
      }

      const response = await fetch(JUDGE_ME_REVIEWS_URL, {
        method: 'POST',
        body,
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error(
          `Judge.me responded with ${response.status}`,
          responseText
        );
        this.#showMessage(this.#getErrorMessage(responseText), 'error');
        this.#setSubmitting(false);
        return;
      }

      form.hidden = true;
      this.#showMessage(Theme.translations?.review_form_success || 'Thanks for your review.', 'success');
    } catch (error) {
      console.error(error);
      this.#showMessage(this.#getErrorMessage(), 'error');
      this.#setSubmitting(false);
    }
  };

  /**
   * Judge.me returns a flat `{ "message": "<human-readable string>" }` body
   * on 4xx responses (e.g. `{"message":"Email must be present"}`), confirmed
   * against the live API during QA. Surface that specific text when present;
   * fall back to the generic translated error otherwise (network failure,
   * non-JSON body, or a response shape we haven't seen).
   *
   * @param {string} [responseText]
   * @returns {string}
   */
  #getErrorMessage(responseText) {
    const fallback = Theme.translations?.review_form_error || 'Something went wrong. Please try again.';

    if (!responseText) return fallback;

    try {
      const data = JSON.parse(responseText);
      if (data && typeof data.message === 'string' && data.message.trim() !== '') {
        return data.message;
      }
    } catch {
      // Not valid JSON — fall through to the generic message.
    }

    return fallback;
  }

  /**
   * @param {boolean} isSubmitting
   */
  #setSubmitting(isSubmitting) {
    if (this.refs.submitButton) {
      this.refs.submitButton.disabled = isSubmitting;
    }
  }

  /**
   * @param {string} text
   * @param {'success' | 'error'} type
   */
  #showMessage(text, type) {
    const message = this.refs.message;
    message.textContent = text;
    message.hidden = false;
    message.classList.toggle('review-form__message--error', type === 'error');
    message.classList.toggle('review-form__message--success', type === 'success');
  }
}

if (!customElements.get('review-form')) {
  customElements.define('review-form', ReviewForm);
}
